import React from 'react';
import './Card.css';

type CardElevation = 'flat' | 'raised' | 'elevated';

interface CardProps {
  children?: React.ReactNode;
  elevation?: CardElevation;
  /** Add default body padding instead of relying on inner layout */
  padded?: boolean;
  interactive?: boolean;
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
}

interface CardHeaderProps {
  children?: React.ReactNode;
  gradient?: boolean;
  className?: string;
}

interface CardTitleProps {
  children?: React.ReactNode;
  subtitle?: React.ReactNode;
  className?: string;
}

interface CardBodyProps {
  children?: React.ReactNode;
  className?: string;
}

interface CardFooterProps {
  children?: React.ReactNode;
  className?: string;
}

function Card({
  children,
  elevation = 'flat',
  padded = false,
  interactive = false,
  className = '',
  style,
  onClick,
}: CardProps) {
  const classes = [
    'ui-card',
    `ui-card--${elevation}`,
    padded ? 'ui-card--padded' : '',
    interactive ? 'ui-card--interactive' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes} style={style} onClick={onClick}>
      {children}
    </div>
  );
}

function CardHeader({ children, gradient = false, className = '' }: CardHeaderProps) {
  const classes = [
    'ui-card__header',
    gradient ? 'ui-card__header--gradient' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return <div className={classes}>{children}</div>;
}

function CardTitle({ children, subtitle, className = '' }: CardTitleProps) {
  return (
    <div className={className}>
      <div className="ui-card__title">{children}</div>
      {subtitle && <div className="ui-card__subtitle">{subtitle}</div>}
    </div>
  );
}

function CardBody({ children, className = '' }: CardBodyProps) {
  return <div className={`ui-card__body ${className}`}>{children}</div>;
}

function CardFooter({ children, className = '' }: CardFooterProps) {
  return <div className={`ui-card__footer ${className}`}>{children}</div>;
}

Card.Header = CardHeader;
Card.Title = CardTitle;
Card.Body = CardBody;
Card.Footer = CardFooter;

export default Card;
